import { inject, Injectable } from "@angular/core";
import { PortalProductStrategy } from "./portal-product-strategy";
import { BehaviorSubject, combineLatest, map, Observable, of } from "rxjs";
import { isKnownProductKey, ProductKey, Products } from "gast-common-ng2";
import { PORTAL_DEFAULT_PRODUCT_TOKEN } from "../../../constants";

@Injectable()
export class AllPossibleProductStrategyService extends PortalProductStrategy {
  readonly #defaultAppProduct: ProductKey = inject(PORTAL_DEFAULT_PRODUCT_TOKEN);
  readonly #currentProduct$$ = new BehaviorSubject<ProductKey>(this.#defaultAppProduct);

  readonly availableProducts$: Observable<ProductKey[]> = of<ProductKey[]>(
    Object.keys(Products) as (keyof typeof Products)[]
  );

  readonly selectedProduct$: Observable<ProductKey> = combineLatest([
    this.availableProducts$,
    this.#currentProduct$$.asObservable(),
  ]).pipe(
    map(([availableProducts, currentProduct]) => {
      const foundProduct: ProductKey | undefined = availableProducts.find((p) => p === currentProduct);
      return foundProduct || currentProduct;
    })
  );

  readonly selectedProductAsString$: Observable<string> = this.selectedProduct$.pipe(
    map((product: ProductKey): string => product.toString())
  );

  readonly availableProductsAsString$: Observable<string[]> = this.availableProducts$.pipe(
    map((products: ProductKey[]): string[] => products.map((product: ProductKey) => product.toString()))
  );

  constructor() {
    super();
    this.subscribeToProductUpdated();
  }

  updateCurrentProduct(productToSet: string): void {
    if (isKnownProductKey(productToSet)) {
      this.#currentProduct$$.next(productToSet);
    } else {
      throw new Error("Trying to set unknown product key.");
    }
  }
}
